import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Grid, TextField, Typography, useMediaQuery } from "@mui/material";

import PhoneNumber from "./../components/PhoneNumber";

import { useAppDispatch } from "../store/hooks";
import {
  changeBusinessCategory,
  changeBusinessName,
  changeEmail,
  changeName,
  changePhoneNumber,
  changeTown,
  EstimateModel,
  selectEstimate,
  selectEstimateStatus,
  setStatusLoading,
  setStatusFailed,
  setStatusSucceeded,
  setEstimateResult,
} from "../store/features/estimateSlice";
import TownSelect from "../components/TownSelect";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import CategorySelect from "../components/CategorySelect";
import FormHelperText from "@mui/material/FormHelperText";
import { checkEmail } from "../utils/helpers";
import { isValidNumber } from "libphonenumber-js";
import { useSnackbar } from "notistack";
import isEmpty from "lodash.isempty";
import { getCategoryAdCopy } from "../store/features/categorySlice";
import Wait from "./Wait";
import { createEstimate } from "../services/estimateService";
import { EstimateAPIModel } from "../models/apiModels";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";

export default function Start() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const state: EstimateModel = useSelector(selectEstimate);
  const estimateStatus = useSelector(selectEstimateStatus);
  const { enqueueSnackbar } = useSnackbar();

  const [errors, setErrors] = useState<any>({});
  const [isFormSubmitted, setFormSubmitted] = useState(false);

  const validate = (state: EstimateModel) => {
    const newErrors: any = {};

    if (state.name == null || state.name.trim() === "") {
      newErrors.name = { required: true };
    }

    if (state.email == null || state.email.trim() === "") {
      newErrors.email = { required: true };
    } else if (!checkEmail(state.email)) {
      newErrors.email = { invalidEmail: true };
    }

    if (
      state.phoneNumber?.number == null ||
      state.phoneNumber.number.trim() === ""
    ) {
      newErrors.phone = { required: true };
    } else if (
      !isValidNumber(state.phoneNumber.number, state.phoneNumber.prefix?.code)
    ) {
      newErrors.phone = { invalidNumber: true };
    }

    if (state.businessName == null || state.businessName.trim() === "") {
      newErrors.businessName = { required: true };
    }

    if (state.businessCategory == null) {
      newErrors.category = { required: true };
    }

    if (state.town == null) {
      newErrors.town = { required: true };
    }

    setErrors(newErrors);
  };

  useEffect(() => {
    validate(state);
  }, [state]);

  async function generateEstimate() {
    setFormSubmitted(true);
    if (!isEmpty(errors)) {
      return;
    }

    dispatch(setStatusLoading());
    const data = new EstimateAPIModel(state);
    try {
      const response = await createEstimate(data);
      dispatch(setEstimateResult(response.results));
      dispatch(setStatusSucceeded());
      navigate(`/estimate`);
    } catch (e) {
      dispatch(setStatusFailed());
      enqueueSnackbar(t("start.estimateFailedError"), {
        variant: "error",
      });
    }
  }

  const visibleErrors = isFormSubmitted ? errors : {};

  if (estimateStatus === "loading") {
    return <Wait />;
  }

  return (
    <Box component="form" noValidate>
      <Grid container spacing={2} justifyContent={"center"}>
        <Grid item xs={12} sm={5}>
          <Typography variant="h6" fontWeight={"bold"} color={"secondary"}>
            {t("start.title")}
          </Typography>
          <Typography marginTop={2} variant="subtitle2" fontWeight={"400"}>
            {t("start.subTitle")}
          </Typography>
        </Grid>
        <Grid
          item
          sm={7}
          xs={12}
          container
          marginBottom={2}
          rowSpacing={sm ? 8 : 5}
          columnSpacing={2}
        >
          <Grid item xs={12} sm={6}>
            <InputLabel htmlFor="name" required variant="standard" shrink>
              {t("start.name")}
            </InputLabel>
            <TextField
              id="name"
              variant="outlined"
              value={state.name}
              error={visibleErrors.name?.required}
              placeholder={t("start.namePlaceholder")}
              required
              fullWidth
              size="small"
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(changeName(ev.currentTarget.value))
              }
            />
            {visibleErrors.name?.required && (
              <FormHelperText error>
                {t("start.validations.requiredField")}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputLabel htmlFor="email" required variant="standard" shrink>
              {t("start.email")}
            </InputLabel>
            <TextField
              id="email"
              size="small"
              required
              fullWidth
              value={state.email}
              error={
                visibleErrors.email?.required ||
                visibleErrors.email?.invalidEmail
              }
              placeholder={t("start.emailPlaceholder")}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(changeEmail(ev.currentTarget.value))
              }
            />
            {visibleErrors.email?.required && (
              <FormHelperText error>
                {t("start.validations.requiredField")}
              </FormHelperText>
            )}
            {visibleErrors.email?.invalidEmail && (
              <FormHelperText error>
                {t("start.validations.invalidEmail")}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneNumber
              phone={state.phoneNumber}
              updatePhone={(value) => dispatch(changePhoneNumber(value))}
              errors={visibleErrors.phone}
              placeholder={t("start.phoneNumberPlaceholder")}
              label={t("start.phoneNumber")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputLabel
              htmlFor="businessName"
              required
              variant="standard"
              shrink
            >
              {t("start.businessName")}
            </InputLabel>
            <TextField
              required
              fullWidth
              size="small"
              id="businessName"
              variant="outlined"
              value={state.businessName}
              error={visibleErrors.businessName?.required}
              placeholder={t("start.businessNamePlaceholder")}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(changeBusinessName(ev.currentTarget.value))
              }
            />
            {visibleErrors.businessName?.required && (
              <FormHelperText error>
                {t("start.validations.requiredField")}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <CategorySelect
              category={state.businessCategory}
              errors={visibleErrors.category}
              updateCategory={(newCategory) => {
                dispatch(changeBusinessCategory(newCategory));
                dispatch(getCategoryAdCopy(newCategory?.key));
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TownSelect
              town={state.town}
              errors={visibleErrors.town}
              updateTown={(newTown) => {
                dispatch(changeTown(newTown));
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={generateEstimate}
              disableElevation
              className="rounded-button"
            >
              <Typography fontWeight={"bold"} marginRight={1}>
                {t("start.generateEstimateAction")}
              </Typography>
              <ArrowForwardIcon />
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
